import React from 'react';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { RevealSection, StaggerGrid, getServiceIcon } from './shared';
import i18n from 'i18next';

export const ServicesSection = ({ sid, theme, services, txt }) => (
  <React.Fragment>
    <RevealSection>
      <section className="py-14 sm:py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0c1222 0%, #0f172a 100%)' }} data-testid="energomat-mini-banner">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #0ea5e9 0%, transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="flex items-center gap-4 mb-3">
                <span className="text-2xl font-extrabold text-white tracking-tight">energo<span className="text-sky-400">mat</span></span>
                <span className="text-[10px] font-bold text-sky-400 bg-sky-500/10 px-3 py-1 rounded-full uppercase tracking-widest">Live odečty</span>
              </div>
              <p className="text-white text-lg font-bold mb-1">Automatické odečty elektroměrů po celé ČR</p>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">Online sběr dat, přehledy a podklady pro vyúčtování — vše bez ruční práce.</p>
              <a href="/?site_id=energomat" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-3 rounded-full transition-all text-sm shadow-lg shadow-sky-500/20 hover:shadow-xl hover:-translate-y-0.5">
                Zajímá mě to <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="md:col-span-2">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /><span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Live</span></div>
                  <a href="/?site_id=energomat" className="text-[10px] text-sky-400 hover:text-sky-300 font-semibold">energomat.cloud →</a>
                </div>
                <div className="flex gap-3 mb-3">
                  {[{ v: '127', u: 'kWh', l: 'Dnes', c: 'text-white' }, { v: '2,840', u: 'kWh', l: 'Měsíc', c: 'text-sky-400' }, { v: '0.4%', u: '', l: 'Odchylka', c: 'text-green-400' }].map((k, i) => (
                    <div key={i} className="flex-1 text-center">
                      <p className={`text-base font-black ${k.c}`}>{k.v}<span className="text-[8px] font-normal text-slate-500 ml-0.5">{k.u}</span></p>
                      <p className="text-[7px] text-slate-500 uppercase tracking-wider">{k.l}</p>
                    </div>
                  ))}
                </div>
                <div className="h-10 flex items-end gap-0.5">{[40, 35, 45, 38, 52, 48, 44, 39, 47, 50, 43, 46].map((v, i) => <div key={i} className="flex-1 rounded-sm" style={{ height: `${v * 1.2}%`, background: 'linear-gradient(to top, #0ea5e9, #06b6d4)', opacity: 0.5 + (i * 0.04) }} />)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </RevealSection>
    <section id={sid} className="py-16 sm:py-28 bg-slate-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealSection className="text-center mb-10 sm:mb-16">
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: `${theme.primary_color}10`, color: theme.primary_color }}>{txt('services', 'badge')}</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: `${theme.font_heading}, sans-serif` }}>{txt('services', 'title')}</h2>
          <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>{txt('services', 'subtitle')}</p>
        </RevealSection>
        <StaggerGrid className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const lang = i18n.language;
            const title = (lang === 'en' && service.title_en) ? service.title_en : (service.title_cs || service.title);
            const description = (lang === 'en' && service.description_en) ? service.description_en : (service.description_cs || service.description);
            return (
              <Card key={service.id} className="card-lift p-8 bg-white border border-slate-100 rounded-2xl group cursor-pointer shadow-md hover:shadow-2xl" data-testid={`service-card-${index}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-lg" style={{ background: `linear-gradient(135deg, ${theme.primary_color}, ${theme.primary_hover})` }}>
                  {getServiceIcon(service.icon)}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-slate-800 transition-colors" style={{ fontFamily: `${theme.font_heading}, sans-serif` }}>{title}</h3>
                <p className="text-slate-500 leading-relaxed" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>{description}</p>
              </Card>
            );
          })}
        </StaggerGrid>
      </div>
    </section>
  </React.Fragment>
);
